import { signal, type Signal } from '@preact/signals';
import type { EntryType } from '@fcbh/atlas-schema';

export type AtlasLink = {
  type: EntryType;
  slug: string;
  surface: string;
};

export type VerseAtlasLinks = Record<string, AtlasLink[]>;

type AtlasIndexFile = {
  formatVersion: number;
  generatedAt: string;
  book: string;
  lang: string;
  atlasHost: string;
  entries: VerseAtlasLinks;
};

const cache = new Map<string, Signal<VerseAtlasLinks>>();
const EMPTY: VerseAtlasLinks = Object.freeze({}) as VerseAtlasLinks;

/**
 * Normalise a book code to the OSIS filename casing used by bake-atlas-index:
 *   first character preserved (handles "1CO" → "1co", "MAT" → "Mat"),
 *   remaining characters lowercased.
 * chapter.bookId arrives as uppercase BB codes ("MAT", "1CO") but the baked
 * files are named "Mat.en.json", "1co.en.json" — a case mismatch that 404s
 * silently on Vercel's case-sensitive Linux filesystem.
 */
function normalizeBook(book: string): string {
  return book.charAt(0) + book.slice(1).toLowerCase();
}

/**
 * Normalise a BCP-47 language tag to the base language code used in filenames.
 * Partner PWAs carry full tags like "en-US" but the baked files use just "en".
 */
function normalizeLang(lang: string): string {
  return lang.split('-')[0]!.toLowerCase();
}

function key(book: string, lang: string): string {
  return `${normalizeBook(book)}.${normalizeLang(lang)}`;
}

/**
 * Publish-time kill switch. The builder injects
 *   window.__ATLAS_ENABLED__ = false
 * into the reader HTML when the project's Atlas config is disabled. When
 * the flag is explicitly false we short-circuit every fetch and return a
 * frozen empty signal, so the reader degrades to plain text with zero
 * network chatter to fcbh-atlas.
 */
function atlasDisabledAtPublishTime(): boolean {
  if (typeof window === 'undefined') return false;
  return (window as unknown as { __ATLAS_ENABLED__?: boolean }).__ATLAS_ENABLED__ === false;
}

/**
 * Returns a signal that resolves to the verse→atlas-links record for a book.
 * The first call kicks off the network fetch; subsequent calls return the same
 * signal. If the file 404s, the signal stays at `{}` — atlas affordances
 * simply don't render, which degrades gracefully.
 */
export function atlasIndexSignal(book: string, lang: string): Signal<VerseAtlasLinks> {
  const k = key(book, lang);
  const existing = cache.get(k);
  if (existing) return existing;
  const s = signal<VerseAtlasLinks>(EMPTY);
  cache.set(k, s);
  if (atlasDisabledAtPublishTime()) {
    // Atlas turned off at publish — no fetch, no listeners, no allocs.
    return s;
  }
  const normBook = normalizeBook(book);
  const normLang = normalizeLang(lang);
  fetch(`/data/atlas-index/${encodeURIComponent(normBook)}.${encodeURIComponent(normLang)}.json`, {
    cache: 'force-cache',
  })
    .then(async (r) => {
      if (!r.ok) return;
      const body = (await r.json()) as AtlasIndexFile;
      if (body && body.entries && typeof body.formatVersion === 'number' && body.formatVersion >= 1) {
        s.value = body.entries;
      }
    })
    .catch(() => {
      // Network/parse failures degrade silently — atlas dots just don't appear.
    });
  return s;
}

/** Synchronously read the currently-loaded links for a verse, or [] if not loaded. */
export function linksForVerse(book: string, lang: string, ref: string): AtlasLink[] {
  return atlasIndexSignal(book, lang).value[ref] ?? [];
}
